import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { getLeagueAvatar } from '@sorare/marketplace/src/lib/newSeason';
import { css } from '@sorare/css';

import type {
  CardPullRulesDialog_cardPullConfig,
  CardPullRulesDialog_cardShardsChest,
} from '#src/components/essence/CardPullExperience/CardPullRulesDialog/__generated__/index.graphql.ts';
import {
  CardPullRulesDialog,
  TabValue,
} from '#src/components/essence/CardPullExperience/CardPullRulesDialog/index.tsx';

import { WithCompetitionStatusDot } from './WithCompetitionStatusDot.tsx';
import type { CompetitionAvailability_cardShardsChest } from './__generated__/index.graphql.ts';
import { splitByPoolAvailability } from './splitByPoolAvailability.ts';
import { useShowsCompetitionStrip } from './useShowsCompetitionStrip.ts';

const scroller = css`
  width: 100%;
  max-width: var(--100vw);
  margin-top: var(--unit);
`;

const strip = css`
  display: flex;
  align-items: center;
  gap: var(--unit);
  flex: none;
  margin: 0 auto;
  padding: calc(var(--half-unit) / 2);
  border-radius: 30px;
  background: var(--c-nd-100);
`;

const segment = css`
  display: flex;
  align-items: center;
  gap: var(--unit);
  flex: none;
  padding: var(--half-unit);
  border-radius: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-black);
  backdrop-filter: blur(32px);
`;

const avatar = css`
  box-sizing: border-box;
  padding: calc(var(--half-unit) / 2);
`;

const AVATAR_SIZE = 24;

type Props = {
  // Don't spread CardPullRulesDialog into this component's own fragment: CardPullInfos already
  // pulls it, and a second spread double-counts the odds tables past the query complexity limit.
  cardShardsChest: CompetitionAvailability_cardShardsChest &
    CardPullRulesDialog_cardShardsChest;
  cardPullConfigs: CardPullRulesDialog_cardPullConfig[];
};

export const CompetitionAvailability = ({
  cardShardsChest,
  cardPullConfigs,
}: Props) => {
  const [open, setOpen] = useState(false);
  const showsCompetitionStrip = useShowsCompetitionStrip();

  if (!showsCompetitionStrip) {
    return null;
  }

  const { availableCompetitions, craftableCompetitions } = cardShardsChest;

  if (craftableCompetitions.length === 0) {
    return null;
  }

  const { inPool, outOfPool } = splitByPoolAvailability(
    craftableCompetitions,
    availableCompetitions
  );

  const renderCompetition = (
    competition: CompetitionAvailability_cardShardsChest['craftableCompetitions'][number],
    available: boolean
  ) => (
    <WithCompetitionStatusDot key={competition.slug} available={available}>
      <LeagueAvatar
        league={getLeagueAvatar({ competition }, 'icon')}
        size={AVATAR_SIZE}
        className={classNames(avatar, { 'opacity-50': !available })}
      />
    </WithCompetitionStatusDot>
  );

  return (
    <>
      <CardPullRulesDialog
        open={open}
        onClose={() => setOpen(false)}
        initialTab={TabValue.AVAILABLE_COMPETITIONS}
        cardShardsChest={cardShardsChest}
        cardPullConfigs={cardPullConfigs}
      />
      <FullViewportScroll className={scroller}>
        <ButtonBase className={strip} onClick={() => setOpen(true)}>
          {inPool.length > 0 && (
            <span className={segment}>
              {inPool.map(competition => renderCompetition(competition, true))}
            </span>
          )}
          {outOfPool.length > 0 && (
            <span className={segment}>
              {outOfPool.map(competition =>
                renderCompetition(competition, false)
              )}
            </span>
          )}
        </ButtonBase>
      </FullViewportScroll>
    </>
  );
};

CompetitionAvailability.fragments = {
  cardShardsChest: gql`
    fragment CompetitionAvailability_cardShardsChest on CardShardsChest {
      id
      availableCompetitions {
        slug
      }
      craftableCompetitions {
        slug
        ...getLeagueAvatar_competition
      }
    }
    ${getLeagueAvatar.fragments.competition}
  ` as TypedDocumentNode<CompetitionAvailability_cardShardsChest>,
};
